import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, RefreshCw, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../utils/cn';

interface CommonActionBarProps {
  // Left side - Date and time (always shown)
  showDateTime?: boolean;
  
  // Left side - Toggle buttons (like Order/Table view)
  leftActions?: React.ReactNode;
  
  // Center section - Navigation controls
  centerContent?: React.ReactNode;
  
  // Center alignment - whether to center the content
  centerAlign?: boolean;
  
  // Right side - Action buttons
  rightActions?: React.ReactNode;
  
  // Bottom section - Filter buttons
  filterButtons?: React.ReactNode;
  
  // Optional refresh functionality
  onRefresh?: () => void;
  
  // Custom styling
  className?: string;
}

const CommonActionBar: React.FC<CommonActionBarProps> = ({
  showDateTime = true,
  leftActions,
  centerContent,
  centerAlign = false,
  rightActions,
  filterButtons,
  onRefresh,
  className,
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format time in 12-hour format
  const time12Hour = format(currentTime, 'hh:mm:ss a');
  const currentDate = format(currentTime, 'MMM dd, yyyy');

  return (
    <div className={cn(
      "mb-6 border border-gray-200 rounded-lg bg-white shadow-sm",
      className
    )}>
      {/* Main Action Bar */}
      <div className="flex items-center justify-between space-x-4 p-4">
        {/* Left side - Toggle buttons or Date and Time Display */}
        <div className="flex items-center space-x-4">
          {leftActions}
          {showDateTime && (
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Clock className="w-4 h-4" />
              <div className="flex flex-col">
                <span className="font-medium">{currentDate}</span>
                <span className="text-xs text-gray-500">{time12Hour}</span>
              </div>
            </div>
          )}
        </div>

        {/* Center section - Custom content */}
        {centerContent && (
          <div className={`flex-1 flex items-center ${centerAlign ? 'justify-center' : 'justify-start'}`}>
            {centerContent}
          </div>
        )}

        {/* Right side - Actions */}
        <div className="flex items-center space-x-2">
          {/* Custom right actions */}
          {rightActions}
          
          {/* Refresh Button */}
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-100 hover:bg-primary-200 text-primary-700 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="text-sm font-medium">Refresh</span>
            </button>
          )}
        </div>
      </div>

      {/* Break Bar and Filter Buttons */}
      {filterButtons && (
        <>
          <div className="border-t border-gray-200"></div>
          <div className="p-4 pt-3">
            {filterButtons}
          </div>
        </>
      )}
    </div>
  );
};

export default CommonActionBar;
